import { planTypes } from '@/common/constants';
import { computedPbxPlans, computedPbxCallingPlans, computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import _ from 'lodash';

/**
 * Check if the account meets Biz Plus free trial upgrade conditions
 * @param {Object} getters - Vuex getters object
 * @returns {boolean} true if meets the conditions, false otherwise
 */
export const isBizPlusFreeTrialAsBasePlanUpgrade = (getters = {}) => {
  const { planType } = getters.getSelectedBasePlanInfo || {};

  // Check if plan type is ZOOM_ONE_BUNDLE
  if (planType !== planTypes.ZOOM_ONE_BUNDLE) {
    return false;
  }

  const { boughtBizPlusPlan = [] } = getters.plansofBizBundle || {};

  if (boughtBizPlusPlan.length === 0) {
    return false;
  }

  // Check if all bought biz plus plans are free trial
  const allBizPlusFreeTrialPlans = _.every(
    boughtBizPlusPlan,
    (plan) => plan.isOnlineFreeTrial || plan.isFreeTrial
  );

  return allBizPlusFreeTrialPlans;
};

/**
 * Generate upgrade list and add list for Biz Plus free trial to paid upgrade
 * @param {Object} params - Parameters object
 * @param {Array} params.boughtBizPlusPlan - Bought Biz Plus plans (free trial)
 * @param {Array} params.selectedBizPlusPlan - Selected Biz Plus plans (paid)
 * @param {Array} params.planList - Order data plan list
 * @returns {Object} { addPlanList, upgradePlanList }
 */
export const generateBizPlusUpgradePayload = ({
  boughtBizPlusPlan = [],
  selectedBizPlusPlan = [],
  planList = [],
  boughtPbxBasePlan = {},
  selectedPbxBasePlan = {},
} = {}) => {
  // Calculate the minimum count between bought and selected plans
  const minCount = Math.min(boughtBizPlusPlan.length, selectedBizPlusPlan.length);

  // Track used selected plans to avoid duplication
  const usedSelectedPlanCodes = new Set();

  const upgradePlanList = [];

  // Process only up to minCount items
  for (let i = 0; i < minCount; i++) {
    const boughtPlan = boughtBizPlusPlan[i];
    const category = boughtPlan.category;

    // First, try to find a matching selected plan by category that hasn't been used
    let selectedPlan = _.find(selectedBizPlusPlan, (sp) =>
      sp.category === category && !usedSelectedPlanCodes.has(sp.plan_code)
    );

    // If no match by category, get any available selected plan that hasn't been used
    if (!selectedPlan) {
      selectedPlan = _.find(selectedBizPlusPlan, (sp) =>
        !usedSelectedPlanCodes.has(sp.plan_code)
      );
    }

    // If we found a selected plan, mark it as used and add to upgrade list
    if (selectedPlan) {
      usedSelectedPlanCodes.add(selectedPlan.plan_code);
      upgradePlanList.push({
        oldPlanCode: boughtPlan.plan_code,
        planCode: selectedPlan.plan_code,
        quantity: selectedPlan.cartInfo?.selected_number || 1,
      });
    }
  }

  // if bought pbx base plan and selected pbx base plan are both set, add the pbx base plan to upgrade list
  if (boughtPbxBasePlan?.plan_code && selectedPbxBasePlan?.plan_code) {
    upgradePlanList.push({
      oldPlanCode: boughtPbxBasePlan.plan_code,
      planCode: selectedPbxBasePlan.plan_code,
      quantity: 1,
    });
  }

  // Collect all planCodes used in upgradePlanList
  const upgradePlanCodes = new Set(upgradePlanList.map(item => item.planCode));

  // Filter planList to exclude plans that are already in upgradePlanList
  const addPlanList = planList.filter(plan => !upgradePlanCodes.has(plan.planCode));

  return {
    addPlanList,
    upgradePlanList,
  };
};

/**
 * Get free trial upgrade payload for Biz Plus (Wrapper function for backward compatibility)
 * @param {Object} getters - Vuex getters object
 * @returns {Object|null} { addPlanList, upgradePlanList } or null if not applicable
 */
export const getFreeTrialUpgradePayloadForBizPlus = (getters = {}) => {
  const { boughtBizPlusPlan = [], selectedBizPlusPlan = [], pbxBasePlan = {}, // zp base plan
    selectedPbxBasePlan = {}, } = getters.plansofBizBundle || {};
  const { planList = [] } = getters.orderDataPayload || {};

  return generateBizPlusUpgradePayload({
    boughtBizPlusPlan,
    selectedBizPlusPlan,
    planList,
    boughtPbxBasePlan: pbxBasePlan,
    selectedPbxBasePlan: selectedPbxBasePlan,
  });
};

/**
 * Check if the account meets Zoom Phone free trial upgrade conditions
 * @param {Object} getters - Vuex getters object
 * @returns {boolean} true if meets the conditions, false otherwise
 */
export const isZoomPhoneFreeTrialUpgrade = (getters = {}) => {
  const { freeTrialPlans = [] } = getters || {};

  // Check if there are any Zoom Phone free trial plans
  const zoomPhoneFreeTrialPlans = computedPbxPlans(freeTrialPlans);

  if (zoomPhoneFreeTrialPlans.length === 0) {
    return false;
  }

  // Check if all Zoom Phone plans in free trial are actually free trials
  const allZpFreeTrialPlans = _.every(
    zoomPhoneFreeTrialPlans,
    (plan) => plan.isOnlineFreeTrial || plan.isFreeTrial
  );

  return allZpFreeTrialPlans;
};

/**
 * Generate upgrade list and add list for Zoom Phone free trial to paid upgrade
 * @param {Object} params - Parameters object
 * @param {Array} params.boughtZpCallingPlans - Bought Zoom Phone calling plans (free trial)
 * @param {Array} params.boughtZpBasePlan - Bought Zoom Phone base plan (free trial)
 * @param {Array} params.selectedZpCallingPlans - Selected Zoom Phone calling plans (paid)
 * @param {Array} params.selectedZpBasePlan - Selected Zoom Phone base plan (paid)
 * @param {Array} params.planList - Order data plan list
 * @returns {Object} { addPlanList, upgradePlanList }
 */
export const generateZoomPhoneUpgradePayload = ({
  boughtZpCallingPlans = [],
  boughtZpBasePlan = {},
  selectedZpCallingPlans = [],
  selectedZpBasePlan = {},
  planList = [],
} = {}) => {
  const upgradePlanList = [];

  // Track used selected plan codes to avoid duplication
  const usedSelectedPlanCodes = new Set();

  // Process calling plans upgrade
  const minCount = Math.min(boughtZpCallingPlans.length, selectedZpCallingPlans.length);

  for (let i = 0; i < minCount; i++) {
    const boughtPlan = boughtZpCallingPlans[i];
    const category = boughtPlan.category;

    // Try to find a matching selected plan by category that hasn't been used
    let selectedPlan = _.find(selectedZpCallingPlans, (sp) =>
      sp.category === category && !usedSelectedPlanCodes.has(sp.plan_code)
    );

    // If no match by category, get any available selected plan that hasn't been used
    if (!selectedPlan) {
      selectedPlan = _.find(selectedZpCallingPlans, (sp) =>
        !usedSelectedPlanCodes.has(sp.plan_code)
      );
    }

    // If we found a selected plan, mark it as used and add to upgrade list
    if (selectedPlan) {
      usedSelectedPlanCodes.add(selectedPlan.plan_code);
      upgradePlanList.push({
        oldPlanCode: boughtPlan.plan_code,
        planCode: selectedPlan.plan_code,
        quantity: selectedPlan.cartInfo?.selected_number || 1,
      });
    }
  }

  // Process base plan upgrade if both bought and selected base plans exist
  if (boughtZpBasePlan?.plan_code && selectedZpBasePlan?.plan_code) {
    upgradePlanList.push({
      oldPlanCode: boughtZpBasePlan.plan_code,
      planCode: selectedZpBasePlan.plan_code,
      quantity: 1,
    });
  }

  // Collect all planCodes used in upgradePlanList
  const upgradePlanCodes = new Set(upgradePlanList.map(item => item.planCode));

  // Filter planList to exclude plans that are already in upgradePlanList
  const addPlanList = planList.filter(plan => !upgradePlanCodes.has(plan.planCode));

  return {
    addPlanList,
    upgradePlanList,
  };
};

/**
 * Get free trial upgrade payload for Zoom Phone (Wrapper function for backward compatibility)
 * @param {Object} getters - Vuex getters object
 * @returns {Object|null} { addPlanList, upgradePlanList } or null if not applicable
 */
export const getFreeTrialUpgradePayloadForZoomPhone = (getters = {}) => {
  const { freeTrialPlans = [], getSelectedPlans = [] } = getters || {};
  const { planList = [] } = getters.orderDataPayload || {};

  // Get bought (free trial) Zoom Phone plans
  const boughtZpPlans = computedPbxPlans(freeTrialPlans);
  const boughtZpCallingPlans = computedPbxCallingPlans(boughtZpPlans);
  const boughtZpBasePlan = computedPbxBasePlans(boughtZpPlans)[0] || {};

  // Get selected (paid) Zoom Phone plans
  const selectedZpPlans = computedPbxPlans(getSelectedPlans);
  const selectedZpCallingPlans = computedPbxCallingPlans(selectedZpPlans);
  const selectedZpBasePlan = computedPbxBasePlans(selectedZpPlans)[0] || {};

  return generateZoomPhoneUpgradePayload({
    boughtZpCallingPlans,
    boughtZpBasePlan,
    selectedZpCallingPlans,
    selectedZpBasePlan,
    planList,
  });
};

/**
 * Generate upgrade payload for ZP Free Trial to Pro Plus
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.zpTrialCallingPlans - ZP trial calling plans
 * @param {Array} params.zpTrialBasePlans - ZP trial base plans
 * @param {Array} params.selectedProPlusPlans - Selected Pro Plus plans
 * @param {Array} params.selectedZpCallingPlans - Selected ZP calling plans (if any, not Pro Plus)
 * @param {Array} params.selectedZpBasePlans - Selected ZP base plans
 * @param {Array} params.planList - Order data plan list
 * @returns {Object} { addPlanList, upgradePlanList }
 */
export const generateZpFreeTrialToProPlusPayload = ({
  zpTrialCallingPlans = [],
  zpTrialBasePlans = [],
  selectedProPlusPlans = [],
  selectedZpCallingPlans = [],
  selectedZpBasePlans = [],
  planList = [],
} = {}) => {
  // Calculate the minimum count between trial and selected Pro Plus plans
  const minCount = Math.min(zpTrialCallingPlans.length, selectedProPlusPlans.length);

  // Track used selected plans to avoid duplication
  const usedSelectedPlanCodes = new Set();

  const upgradePlanList = [];

  // Process only up to minCount items for calling plan → Pro Plus upgrade
  for (let i = 0; i < minCount; i++) {
    const trialCallingPlan = zpTrialCallingPlans[i];
    const category = trialCallingPlan.category;

    // First, try to find a matching Pro Plus plan by category that hasn't been used
    let selectedProPlus = _.find(selectedProPlusPlans, (sp) =>
      sp.category === category && !usedSelectedPlanCodes.has(sp.plan_code)
    );

    // If no match by category, get any available Pro Plus plan that hasn't been used
    if (!selectedProPlus) {
      selectedProPlus = _.find(selectedProPlusPlans, (sp) =>
        !usedSelectedPlanCodes.has(sp.plan_code)
      );
    }

    // If we found a selected Pro Plus plan, mark it as used and add to upgrade list
    if (selectedProPlus) {
      usedSelectedPlanCodes.add(selectedProPlus.plan_code);
      upgradePlanList.push({
        oldPlanCode: trialCallingPlan.plan_code,
        planCode: selectedProPlus.plan_code,
        quantity: selectedProPlus.cartInfo?.selected_number || 1,
      });
    }
  }

  // If there are selected ZP calling plans (not Pro Plus), upgrade to them
  if (selectedZpCallingPlans.length > 0 && zpTrialCallingPlans.length > selectedProPlusPlans.length) {
    for (let i = selectedProPlusPlans.length; i < zpTrialCallingPlans.length; i++) {
      const trialCallingPlan = zpTrialCallingPlans[i];
      const selectedZpCalling = selectedZpCallingPlans[i - selectedProPlusPlans.length];

      if (selectedZpCalling) {
        upgradePlanList.push({
          oldPlanCode: trialCallingPlan.plan_code,
          planCode: selectedZpCalling.plan_code,
          quantity: selectedZpCalling.cartInfo?.selected_number || 1,
        });
        usedSelectedPlanCodes.add(selectedZpCalling.plan_code);
      }
    }
  }

  // Handle base plan upgrade only if plan_code is different
  // (Usually base plan is the same in ZP FT and Pro Plus, so this rarely triggers)
  if (zpTrialBasePlans.length > 0 && selectedZpBasePlans.length > 0) {
    const trialBasePlan = zpTrialBasePlans[0];
    const selectedBasePlan = selectedZpBasePlans[0];

    // Only add to upgradeList if plan_code is different
    upgradePlanList.push({
      oldPlanCode: trialBasePlan.plan_code,
      planCode: selectedBasePlan.plan_code,
      quantity: 1,
    });
    usedSelectedPlanCodes.add(selectedBasePlan.plan_code);
  }

  // Collect all planCodes used in upgradePlanList
  const upgradePlanCodes = new Set(upgradePlanList.map(item => item.planCode));

  // Filter planList to exclude plans that are already in upgradePlanList
  const addPlanList = planList.filter(plan => !upgradePlanCodes.has(plan.planCode));

  return {
    addPlanList,
    upgradePlanList,
  };
};

/**
 * Get free trial upgrade payload for ZP FT to Pro Plus (Wrapper function)
 * @param {Object} getters - Vuex getters object
 * @returns {Object|null} { addPlanList, upgradePlanList } or null if not applicable
 */
export const getFreeTrialUpgradePayloadForZpToProPlus = (getters = {}) => {
  const { freeTrialPlans = [], getSelectedPlans = [] } = getters || {};
  const { planList = [] } = getters.orderDataPayload || {};

  // Get ZP trial plans
  const zpFreeTrialPlans = computedPbxPlans(freeTrialPlans);
  const zpTrialCallingPlans = computedPbxCallingPlans(zpFreeTrialPlans);
  const zpTrialBasePlans = computedPbxBasePlans(zpFreeTrialPlans);

  // Get selected plans
  const selectedZpPlans = computedPbxPlans(getSelectedPlans);
  const selectedZpCallingPlans = computedPbxCallingPlans(selectedZpPlans);
  const selectedZpBasePlans = computedPbxBasePlans(selectedZpPlans);
  const selectedProPlusPlans = getSelectedPlans.filter(plan => plan.type === planTypes.ZONE_PRO_PLUS);

  return generateZpFreeTrialToProPlusPayload({
    zpTrialCallingPlans,
    zpTrialBasePlans,
    selectedProPlusPlans,
    selectedZpCallingPlans,
    selectedZpBasePlans,
    planList,
  });
};
